import { TypedDocumentNode, gql } from '@apollo/client';
import { Helmet } from 'react-helmet';
import { generatePath, useParams } from 'react-router-dom';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import defaultShield from '@sorare/core/src/assets/club/shield_none.png';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import {
  MainAsideLayout,
  Section,
} from '@sorare/core/src/components/layout/MainAsideLayout';
import { Header } from '@sorare/core/src/components/seo/Header';
import { OfficialProduct } from '@sorare/core/src/components/seo/OfficialProduct';
import { FRONTEND_ASSET_HOST } from '@sorare/core/src/constants/assets';
import {
  FOOTBALL_COUNTRY_SHOW,
  FOOTBALL_LEAGUE_SHOW,
} from '@sorare/core/src/constants/routes';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { metadatas } from '@sorare/core/src/lib/seo/football';

import { SeasonStandings } from 'pages/seo/SeasonStandings';

import { TopPlayers } from './TopPlayers';
import {
  FootballLeagueOverviewQuery,
  FootballLeagueOverviewQueryVariables,
} from './__generated__/page.graphql';

const FOOTBALL_LEAGUE_OVERVIEW_QUERY = gql`
  query FootballLeagueOverviewQuery($slug: String!) {
    football {
      competition(slug: $slug) {
        slug
        displayName
        pictureUrl
        released
        backgroundColor
        country {
          slug
          name
        }
        seasons {
          id
          startYear
        }
        ...Header_subscription
      }
    }
  }
  ${Header.fragments.subscription}
` as TypedDocumentNode<
  FootballLeagueOverviewQuery,
  FootballLeagueOverviewQueryVariables
>;

const Overview = () => {
  const { slug } = useParams();
  const { up: isLaptopAndAbove } = useScreenSize('laptop');

  const { data, loading } = useQuery(FOOTBALL_LEAGUE_OVERVIEW_QUERY, {
    variables: { slug: slug || '' },
    skip: !slug,
  });

  useTitleAndDescription(
    metadatas.leagueOverview.title,
    metadatas.leagueOverview.description,
    {
      league: data?.football.competition.displayName,
    },
    {
      img: data?.football.competition.pictureUrl,
    }
  );

  if (loading) return <LoadingIndicator fullHeight />;
  if (!data) return null;

  const { competition } = data.football;

  const blocks = {
    header: (
      <>
        <Header
          id={competition.slug}
          variant="team"
          displayName={competition.displayName}
          pictureUrl={competition.pictureUrl || defaultShield}
          color={competition.backgroundColor}
          memberships={[
            ...(competition.country
              ? [
                  {
                    name: competition.country.name,
                    pictureUrl: `${FRONTEND_ASSET_HOST}/flags/${competition.country.slug}.svg`,
                    to: generatePath(FOOTBALL_COUNTRY_SHOW, {
                      countryCode: competition.country.slug,
                    }),
                  },
                ]
              : []),
          ]}
          subscription={competition}
        />
        <Helmet
          script={[
            {
              type: 'application/ld+json',
              innerHTML: JSON.stringify({
                '@context': 'https://schema.org/',
                '@type': 'SportsOrganization',
                name: competition.displayName,
                mainEntityOfPage: generatePath(FOOTBALL_LEAGUE_SHOW, {
                  slug: competition.slug,
                }),
                sport: Sport.FOOTBALL,
                image: competition.pictureUrl,
              }),
            },
          ]}
        />
      </>
    ),
    topPlayers: <TopPlayers competitionSlug={competition.slug} />,
    seasonStandings: (
      <SeasonStandings
        displayName={competition.displayName}
        competitionSlug={competition.slug}
        seasons={competition.seasons}
      />
    ),
    officialProduct: competition.released && (
      <OfficialProduct
        displayName={competition.displayName}
        pictureUrl={competition.pictureUrl}
        to={generatePath(FOOTBALL_LEAGUE_SHOW, { slug: competition.slug })}
      />
    ),
  };

  if (isLaptopAndAbove) {
    return (
      <MainAsideLayout
        header={blocks.header}
        aside={[blocks.topPlayers, blocks.officialProduct]}
      >
        {blocks.seasonStandings}
      </MainAsideLayout>
    );
  }

  return (
    <Section>
      {blocks.header}
      {blocks.seasonStandings}
      {blocks.topPlayers}
      {blocks.officialProduct}
    </Section>
  );
};

export default Overview;
